import FacebookIcon from '@mui/icons-material/Facebook';
import InstagramIcon from '@mui/icons-material/Instagram';
import TwitterIcon from '@mui/icons-material/Twitter';
import { Box, GridLegacy as Grid, Link, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import OpenHelpCentreLink from 'components/common/OpenHelpCentreLink';
import { useAnalytics } from 'providers/AnalyticsProvider';
import React from 'react';
import { FaMedium } from 'react-icons/fa';

import SeychellesFlag from '../../assets/icons/SeychellesFlag';
import { appConfig } from '../../config/runtimeConfig';
import { useConsent } from '../../context/ConsentContext';
import { getDynamicBranding } from '../../theme/theme';

interface OwnProps {
  maxwidth: any;
}

const DefList = styled('dl')(({ theme }) => ({
  margin: `0 0 ${theme.spacing(4)}`,
}));
const DefListTitle = styled('dt')(({ theme }) => ({
  margin: `0 0 ${theme.spacing(4)}`,
}));
const DefListItem = styled('dd')(({ theme }) => ({
  margin: `0 0 ${theme.spacing(3)}`,
}));

const SubfooterText = styled(Typography)(({ theme }) => ({
  fontSize: '0.9em',
  letterSpacing: '1px',
  textTransform: 'uppercase',
  color: theme.palette.grey[500],
}));

const Footer: React.FC<OwnProps> = (props) => {
  // Responsive values below are CSS breakpoints, not useMediaQuery(), so
  // server-rendered pages get the right layout before hydration (see
  // HeaderNav).

  const analytics = useAnalytics();
  const { resetConsent } = useConsent();

  const StyledFooter = styled(Grid)(({ theme }) => ({
    backgroundColor: '#fff',
    width: '100%',
    padding: '24px 24px 15px',
    [theme.breakpoints.up('md')]: { padding: '40px 50px 25px' },
    display: 'flex',
    justifyContent: 'center',
    flexWrap: 'wrap',
  }));

  const SubFooter = styled(Grid)(({ theme }) => ({
    width: '100%',
    padding: '15px 24px',
    [theme.breakpoints.up('md')]: { padding: '15px 50px' },
    display: 'flex',
    justifyContent: 'center',
    flexWrap: 'wrap',
    borderTop: `2px solid ${theme.palette.grey[200]}`,
    maxWidth: '2520px !important',
  }));

  const InnerWrapper = styled(Box)(() => ({
    width: '100%',
    maxWidth: props.maxwidth,
  }));
  const { brand, logoLarge, logoLargeWidth, logoLargeHeight } =
    getDynamicBranding();

  const currentYear = new Date().getFullYear();

  return (
    <React.Fragment>
      <StyledFooter id="footer">
        <InnerWrapper>
          <Grid
            container
            spacing={8}
            justifyContent={'space-between'}
            sx={{ flexDirection: { xs: 'column', sm: 'row' } }}
          >
            <Grid
              item
              sm={3}
              md={3}
              sx={{ display: { xs: 'none', md: 'block' } }}
            >
              {/* lazy: never fetched below md, where it's hidden */}
              <img
                alt="Ellipsis Logo"
                width={logoLargeWidth}
                height={logoLargeHeight}
                loading="lazy"
                src={logoLarge}
                // height:auto keeps the aspect ratio: with the height
                // attribute set, maxHeight alone would stretch the image.
                style={{ maxWidth: '45%', maxHeight: '160px', height: 'auto' }}
              />
            </Grid>
            <Grid item md={3} lg={'auto'}>
              <DefList>
                <DefListTitle>
                  <Typography variant="h6" component="h2">
                    {`Explore ${brand}`}{' '}
                  </Typography>
                </DefListTitle>
                <DefListItem>
                  <Typography variant="body1">
                    <Link
                      href="/about-us"
                      onClick={() =>
                        analytics.trackEvent('Click Who we are', {
                          link: 'about-us',
                        })
                      }
                    >
                      Who we are
                    </Link>
                  </Typography>
                </DefListItem>
                <DefListItem>
                  <Typography variant="body1">
                    <Link
                      href="https://help.ellipsis.sc/hc/1313306050"
                      onClick={(event) => {
                        analytics.trackEvent('Click footer help', {
                          link: 'https://help.ellipsis.sc/hc/1313306050',
                        });
                        OpenHelpCentreLink(event);
                      }}
                      data-cy="help-centre-button"
                    >
                      Help
                    </Link>
                  </Typography>
                </DefListItem>
                <DefListItem>
                  <Typography variant="body1">
                    <Link
                      href="/trust-and-safety"
                      onClick={() =>
                        analytics.trackEvent('Click Trust and safety', {
                          link: 'trust-and-safety',
                        })
                      }
                    >
                      Trust and Safety
                    </Link>
                  </Typography>
                </DefListItem>
                <DefListItem>
                  <Typography variant="body1">
                    <Link
                      href="/press"
                      onClick={() =>
                        analytics.trackEvent('Click Press', { link: 'press' })
                      }
                    >
                      Press
                    </Link>
                  </Typography>
                </DefListItem>
              </DefList>
            </Grid>
            <Grid item md={3} lg={'auto'}>
              <DefList>
                <DefListTitle>
                  <Typography variant="h6" component="h2">
                    Factual &amp; Legal{' '}
                  </Typography>
                </DefListTitle>
                <DefListItem>
                  <Typography variant="body1">
                    <Link
                      href="/terms-and-conditions"
                      onClick={() =>
                        analytics.trackEvent('Click Terms and conditions', {
                          link: 'terms-and-conditions',
                        })
                      }
                    >
                      Terms and Conditions
                    </Link>
                  </Typography>
                </DefListItem>
                <DefListItem>
                  <Typography variant="body1">
                    <Link
                      href="/privacy-policy"
                      onClick={() =>
                        analytics.trackEvent('Click Privacy policy', {
                          link: 'privacy-policy',
                        })
                      }
                    >
                      Privacy Policy
                    </Link>
                  </Typography>
                </DefListItem>
                <DefListItem>
                  <Typography variant="body1">
                    <Link
                      href="/guest-conduct-policy"
                      onClick={() =>
                        analytics.trackEvent('Click Guest conduct policy', {
                          link: 'guest-conduct-policy',
                        })
                      }
                    >
                      Guest Conduct Policy
                    </Link>
                  </Typography>
                </DefListItem>
                <DefListItem>
                  <Typography variant="body1">
                    <Link
                      href="/cancellation-policy"
                      onClick={() =>
                        analytics.trackEvent(
                          'Click Cancellation policy - Footer',
                          {
                            link: 'cancellation-policy',
                          }
                        )
                      }
                    >
                      Cancellation Policy
                    </Link>
                  </Typography>
                </DefListItem>
                <DefListItem>
                  <Typography variant="body1">
                    <Link
                      component="button"
                      type="button"
                      onClick={() => {
                        analytics.trackEvent('Click Cookie preferences', {
                          link: 'cookie-preferences',
                        });
                        resetConsent();
                      }}
                      sx={{
                        background: 'none',
                        border: 'none',
                        p: 0,
                        cursor: 'pointer',
                      }}
                      data-cy="cookie-preferences"
                    >
                      Cookie Preferences
                    </Link>
                  </Typography>
                </DefListItem>
              </DefList>
            </Grid>
            <Grid item md={3} lg={'auto'}>
              <DefList>
                <DefListTitle>
                  <Typography variant="h6" component="h2">
                    Contact Us{' '}
                  </Typography>
                </DefListTitle>
                <DefListItem>
                  <Typography variant="body1">
                    <Link
                      href="/contact"
                      onClick={() =>
                        analytics.trackEvent('Click Write', { link: 'contact' })
                      }
                    >
                      Write
                    </Link>
                  </Typography>
                </DefListItem>
                <DefListItem>
                  <Typography variant="body1">
                    <Link
                      href="/contact"
                      onClick={() =>
                        analytics.trackEvent('Click Email', { link: 'contact' })
                      }
                    >
                      Email
                    </Link>
                  </Typography>
                </DefListItem>
                <DefListItem>
                  <Typography variant="body1">
                    <Link
                      href="/contact"
                      onClick={() =>
                        analytics.trackEvent('Click Call', { link: 'contact' })
                      }
                    >
                      Call
                    </Link>
                  </Typography>
                </DefListItem>
              </DefList>
            </Grid>
            <Grid item sm={12} lg={'auto'}>
              <Grid
                container
                alignContent={'flex-start'}
                spacing={{ sm: 10, lg: 0 }}
                sx={{ justifyContent: { xs: 'center', md: 'space-between' } }}
              >
                <Grid item lg={6} md={3}>
                  <Link
                    href="https://www.instagram.com/ellipsismarine/"
                    target="_blank"
                    aria-label="Instagram"
                  >
                    <InstagramIcon sx={{ fontSize: '50px' }} />
                  </Link>
                </Grid>
                <Grid item lg={6} md={3}>
                  <Link
                    href="https://www.facebook.com/ellipsismarine/"
                    target="_blank"
                    aria-label="Facebook"
                  >
                    <FacebookIcon sx={{ fontSize: '50px' }} />
                  </Link>
                </Grid>
                <Grid item lg={6} md={3}>
                  <Link
                    href="https://twitter.com/ellipsismarine/"
                    target="_blank"
                    aria-label="Twitter"
                  >
                    <TwitterIcon sx={{ fontSize: '50px' }} />
                  </Link>
                </Grid>
                <Grid item lg={6} md={3}>
                  <Link
                    href="https://medium.com/ellipsis-marine/"
                    target="_blank"
                    aria-label="Medium"
                  >
                    <FaMedium style={{ fontSize: '50px' }} />
                  </Link>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </InnerWrapper>
      </StyledFooter>
      <SubFooter container spacing={2}>
        {appConfig.APP_BRAND === 'ellipsis' && (
          <Grid item sm={12} md={5} sx={{ width: '100%' }}>
            <SubfooterText
              variant="body1"
              sx={{ textAlign: { xs: 'center', sm: 'left' } }}
            >
              <SeychellesFlag /> Proudly 100% Seychellois owned.&nbsp;
            </SubfooterText>
          </Grid>
        )}
        <Grid item sm={12} md={7}>
          <SubfooterText
            variant="body1"
            sx={{ textAlign: { xs: 'center', sm: 'right' } }}
          >
            © {currentYear} Ellipsis Marine (Proprietary) Limited. All Rights
            Reserved.
          </SubfooterText>
        </Grid>
      </SubFooter>
    </React.Fragment>
  );
};

export default Footer;
